import React from 'react';
import useAuthContext from '../../hooks/useAuthContext';
import { Title, Loader, ReloadIconButton, FarmSelectModal, ClearFilters } from '../../elements';
import {
  HealthSystemDomainFarmsResultFarmViewModel,
  HealthSystemDomainChipNumbersResultChipNumberViewModel,
  HealthSystemDomainChipNumbersResultChipNumberConflict
} from '../../api/HealthSystemApi';
import { useConfirmation } from '../../hooks/ConfirmationService';
import { DEFAULT_ROWS_PAGE_OPTIONS, DEFAULT_ROWS_PER_PAGE, LoaderSize } from '../../constants/constants';
import { extractApiErrorMessage, notifications } from '../../utils';
import {
  Button,
  Chip,
  Icon,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  useTheme
} from '@mui/material';
import { LegacyGrid as Grid } from '../../elements';
import type { Theme } from '@mui/material/styles';
import { colors } from '../../utils';
import { V4_BUTTON_DEFAULT, V4_ICON_BUTTON_SMALL, V4_TABLE } from '../../theme/v4Compat';
import { useTranslation } from 'react-i18next';
import UserRole from '../../constants/user-role';
import { ApiService } from '../../hooks';
import CreateUpdateChipNumberModal from '../CreateUpdateChipNumberModal';
import type { ChipNumberDraft } from '../CreateUpdateChipNumberModal/component';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AnimalGender } from '../../models/animal';
import { formatChipNumber } from '../../utils/chipNumberMask';
import { formatDate, formatDateTime } from '../../utils/dateHelper';
import { faVenus, faMars, faVenusMars } from '@fortawesome/free-solid-svg-icons';

// Create mode for CreateUpdateChipNumberModal: nothing set yet, not even an id.
const EMPTY_CHIP_NUMBER: ChipNumberDraft = {};

const ChipNumbersPage: React.FC<any> = () => {
  const styles = sxStyles(useTheme());
  const { state } = useAuthContext();
  const { t } = useTranslation();
  const confirmDialog = useConfirmation();

  const apiService = new ApiService();

  const [employees, setChipNumbers] = React.useState<HealthSystemDomainChipNumbersResultChipNumberViewModel[]>([]);
  const [overlappingRanges, setOverlappingRanges] = React.useState<
    HealthSystemDomainChipNumbersResultChipNumberConflict[]
  >([]);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const [employeeModalOpened, setChipNumberModalOpened] = React.useState(false);
  const [selectedChipNumber, setSelectedChipNumber] = React.useState<ChipNumberDraft>(EMPTY_CHIP_NUMBER);

  const [rangeFrom, setRangeFrom] = React.useState('');
  const [rangeTo, setRangeTo] = React.useState('');
  const [selectedFarm, setSelectedFarm] = React.useState<HealthSystemDomainFarmsResultFarmViewModel | null>(null);

  const handleResetFilters = () => {
    setRangeFrom('');
    setRangeTo('');
    setSelectedFarm(null);
    setPage(0);
    setLoaded(false);
  };

  // pagination
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(DEFAULT_ROWS_PER_PAGE);
  const [total, setTotal] = React.useState(0);

  const handleChangePage = (_event: unknown, newPage: number) => {
    setPage(newPage);
    setLoaded(false);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
    setLoaded(false);
  };
  // * pagination

  const loadChipNumbers = React.useCallback(async () => {
    const searchPage = page === 0 ? 1 : page + 1;
    setIsBusy(true);

    const filterParams: Record<string, unknown> = { Page: searchPage, Size: rowsPerPage };
    if (rangeFrom !== '') filterParams['Filter.RangeFrom'] = Number(rangeFrom);
    if (rangeTo !== '') filterParams['Filter.RangeTo'] = Number(rangeTo);
    if (selectedFarm) filterParams['Filter.FarmIds'] = [selectedFarm.id];

    apiService.api.chipNumbers
      .chipNumbersGetChipNumber(
        filterParams as Parameters<typeof apiService.api.chipNumbers.chipNumbersGetChipNumber>[0]
      )
      .then((result) => {
        setChipNumbers(result.data.data);
        setOverlappingRanges(result.data.overlappingRanges ?? []);
        setTotal(result.data.total);
      })
      .catch((e) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => {
        setIsBusy(false);
        setLoaded(true);
      });
  }, [page, rowsPerPage, rangeFrom, rangeTo, selectedFarm]);

  React.useEffect(() => {
    if (loaded === false) {
      const loadData = async () => loadChipNumbers();
      loadData();
    }
  }, [loaded, page, rowsPerPage]);

  const handleRemoveChipNumber = React.useCallback(
    (chipNumber: HealthSystemDomainChipNumbersResultChipNumberViewModel) => {
      setSelectedChipNumber(EMPTY_CHIP_NUMBER);
      confirmDialog({
        dialogId: 'delete-chip-numbers-dialog',
        title: t('f-chip-numbers-del-title'),
        message: t('f-chip-numbers-del-msg', { name: chipNumber.start + ' - ' + chipNumber.end }),
        alertSeverity: 'warning'
      }).then(() => {
        apiService.api.chipNumbers
          .chipNumbersDeleteChipNumber({ id: chipNumber.id })
          .then(() => {
            notifications.success(t('f-chip-numbers-del-ok'));
            setLoaded(false);
          })
          .catch((e) => {
            notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
          });
      });
    },
    []
  );

  const handleAddChipNumber = () => {
    setSelectedChipNumber(EMPTY_CHIP_NUMBER); // Reset selectedChipNumber
    setChipNumberModalOpened(true);
  };

  const handleEditChipNumber = React.useCallback((row: HealthSystemDomainChipNumbersResultChipNumberViewModel) => {
    setSelectedChipNumber(row);
    setChipNumberModalOpened(true);
  }, []);

  const handleOnChipNumber = (result: boolean) => {
    if (result) {
      setLoaded(false);
    }
    setChipNumberModalOpened(false);
  };

  const renderRange = (row: HealthSystemDomainChipNumbersResultChipNumberViewModel) => {
    const start = row.start.toString().padStart(8, '0');
    const end = row.end.toString().padStart(8, '0');
    return formatChipNumber(start) + ' - ' + formatChipNumber(end);
  };

  const renderGenderIcon = (gender: AnimalGender) => {
    switch (gender) {
      case AnimalGender.Male:
        return (
          <>
            <FontAwesomeIcon icon={faMars} size="1x" /> {t('f-gender-male')}
          </>
        );
      case AnimalGender.Female:
        return (
          <>
            <FontAwesomeIcon icon={faVenus} size="1x" /> {t('f-gender-female')}
          </>
        );
      default:
        return (
          <>
            <FontAwesomeIcon icon={faVenusMars} size="1x" /> {t('f-any')}
          </>
        );
    }
  };

  return (
    <>
      <Grid container>
        <Grid item xs={12} md={12}>
          <Grid container direction="row" sx={styles.paperHeader}>
            <Grid item container md={6} xs={10} spacing={1}>
              <Grid item>
                <Icon fontSize="large" color="primary">
                  {'groups'}
                </Icon>
              </Grid>
              <Grid item>
                <Title>{t('f-chip-numbers')}</Title>
              </Grid>
              <Grid item>
                <Chip label={total} variant="outlined" />
              </Grid>
            </Grid>

            <Grid item container sx={styles.justifyRight} md={6} xs={10}>
              <Grid item>
                <ReloadIconButton action={() => setLoaded(false)} />
              </Grid>

              <Grid item>
                <Button
                  disabled={state.role === UserRole.user}
                  id="addChipNumberBtn"
                  size="small"
                  sx={{ ...V4_BUTTON_DEFAULT, ...styles.withMarginLeft }}
                  color="inherit"
                  variant="contained"
                  startIcon={<Icon>{'add_circle_outline'}</Icon>}
                  onClick={handleAddChipNumber}
                >
                  {t('f-chip-numbers-add-bnt')}
                </Button>
              </Grid>
            </Grid>
          </Grid>

          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 my-3">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500 whitespace-nowrap">{t('f-chip-numbers-range')}:</span>
                <TextField
                  placeholder={t('f-chip-numbers-filter-range-from')}
                  type="number"
                  size="small"
                  variant="outlined"
                  value={rangeFrom}
                  onChange={(e) => {
                    setRangeFrom(e.target.value);
                    setPage(0);
                    setLoaded(false);
                  }}
                  slotProps={{ htmlInput: { min: 0, style: { width: 130 } } }}
                />
                <span className="text-gray-400">–</span>
                <TextField
                  placeholder={t('f-chip-numbers-filter-range-to')}
                  type="number"
                  size="small"
                  variant="outlined"
                  value={rangeTo}
                  onChange={(e) => {
                    setRangeTo(e.target.value);
                    setPage(0);
                    setLoaded(false);
                  }}
                  slotProps={{ htmlInput: { min: 0, style: { width: 130 } } }}
                />
              </div>

              <div style={{ width: 200 }}>
                <FarmSelectModal
                  farm={selectedFarm}
                  onChange={(farm) => {
                    setSelectedFarm(farm);
                    setPage(0);
                    setLoaded(false);
                  }}
                />
              </div>

              <ClearFilters onClick={handleResetFilters} />
            </div>
          </div>

          <Loader isLoading={isBusy} size={LoaderSize.DEFAULT_SIZE} />

          {/* data table */}
          {loaded && (
            <Grid container spacing={3}>
              <Grid item md={12}>
                <Paper elevation={1}>
                  <Table size="small" sx={V4_TABLE}>
                    <TableHead sx={styles.tableHeader}>
                      <TableRow>
                        <TableCell>{t('f-chip-numbers-range')}</TableCell>
                        <TableCell>{t('f-chip-numbers-farm')}</TableCell>
                        <TableCell>{t('f-chip-numbers-gender')}</TableCell>
                        <TableCell>{t('f-chip-numbers-created')}</TableCell>
                        <TableCell>{t('f-chip-numbers-overlaps')}</TableCell>
                        <TableCell />
                      </TableRow>
                    </TableHead>

                    <TableBody>
                      {employees.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{renderRange(row)}</TableCell>
                          <TableCell>{row.farm?.name}</TableCell>
                          <TableCell>{renderGenderIcon(row.gender)}</TableCell>
                          <TableCell>
                            <Tooltip arrow title={formatDateTime(row.created)}>
                              <span>{formatDate(row.created)}</span>
                            </Tooltip>
                          </TableCell>
                          <TableCell>
                            {(() => {
                              const conflicts = overlappingRanges.filter(
                                (c) => c.first.id === row.id || c.second.id === row.id
                              );
                              if (conflicts.length === 0) return null;
                              const tooltipContent = (
                                <div>
                                  {conflicts.map((c, i) => {
                                    const other = c.first.id === row.id ? c.second : c.first;
                                    return (
                                      <div key={i}>
                                        {formatChipNumber(other.start)} – {formatChipNumber(other.end)}
                                        {other.farm?.name ? ` (${other.farm.name})` : ''}
                                      </div>
                                    );
                                  })}
                                </div>
                              );
                              return (
                                <Tooltip arrow title={tooltipContent}>
                                  <Icon style={{ color: '#f59e0b', fontSize: 20, cursor: 'default' }}>warning</Icon>
                                </Tooltip>
                              );
                            })()}
                          </TableCell>
                          <TableCell align="right">
                            <Grid container direction="row">
                              <Tooltip arrow title={t('f-com-edit')}>
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleEditChipNumber(row)}
                                  sx={V4_ICON_BUTTON_SMALL}
                                >
                                  <Icon>{'edit_outline'}</Icon>
                                </IconButton>
                              </Tooltip>
                              <Tooltip arrow title={t('f-com-remove')}>
                                <IconButton
                                  color="primary"
                                  size="small"
                                  onClick={() => handleRemoveChipNumber(row)}
                                  sx={V4_ICON_BUTTON_SMALL}
                                >
                                  <Icon>{'delete_outline'}</Icon>
                                </IconButton>
                              </Tooltip>
                            </Grid>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>

                    <TableFooter>
                      <TableRow>
                        <TablePagination
                          rowsPerPageOptions={DEFAULT_ROWS_PAGE_OPTIONS}
                          colSpan={7}
                          count={total}
                          rowsPerPage={rowsPerPage}
                          page={page}
                          onPageChange={handleChangePage}
                          onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                      </TableRow>
                    </TableFooter>
                  </Table>
                </Paper>
              </Grid>
            </Grid>
          )}
        </Grid>
      </Grid>

      <CreateUpdateChipNumberModal
        key={employeeModalOpened ? 'add-new' : selectedChipNumber.id}
        opened={employeeModalOpened}
        employee={selectedChipNumber}
        onModalAction={handleOnChipNumber}
      />
    </>
  );
};

export default ChipNumbersPage;

/** Was ./styles.ts (makeStyles); v9 `sx` values. */
const sxStyles = (theme: Theme) =>
  ({
    paper: {
      padding: theme.spacing(2),
      display: 'flex',
      overflow: 'auto',
      flexDirection: 'column'
    },
    tableHeader: {
      background: colors.defaultGrey
    },
    tableHeaderFooter: {
      background: colors.defaultGrey
    },
    paperHeader: {
      justifyContent: 'space-between'
    },
    justifyRight: {
      justifyContent: 'flex-end',
      alignItems: 'center'
    },
    withMarginLeft: {
      marginLeft: theme.spacing(1)
    },
    tableInfoChip: {
      marginLeft: theme.spacing(2),
      background: colors.defaultGrey
    }
  }) as const;
